---
title: Začetek
sidebar_position: 2
description: Začetek
---

# Začetek

## Prijava

### Prva prijava z računom Planet

Če imate račun Planet, za dostop do aplikacije AM sledite tem korakom:

#### Korak 1: Odprite aplikacijo

1. Odprite spletni brskalnik
2. Pojdite na [https://area-monitoring.planet.com](https://area-monitoring.planet.com)
3. Prikaže se prijavni zaslon

![Prijavni zaslon aplikacije AM z označenim gumbom za račun Planet](../imgs/login-planet.png)

#### Korak 2: Izberite prijavo z računom Planet

1. Na prijavnem zaslonu kliknite gumb **»Planet Account«**
2. Preusmerjeni boste na overovitveno stran Planet

#### Korak 3: Povezovanje računov (samo ob prvi prijavi)

Če se z računom Planet prijavljate prvič:

1. Po overitvi prek Planet se prikaže zaslon za povezovanje računov
2. Izberite možnost **»Add to existing account«**
3. Na vaš e-poštni naslov bo z naslova `noreply@planet.com` poslano potrditveno sporočilo
4. **Pomembno**: Preverite e-pošto in kliknite potrditveno povezavo v **5 minutah**
5. Če povezava poteče, boste morali prijavni postopek ponoviti od začetka

#### Korak 4: Sprejem pogojev uporabe

1. Preberite pogoje uporabe Planet Labs
2. Označite potrditveno polje
3. Za nadaljevanje kliknite `Accept`

#### Korak 5: Dostop odobren

Po overitvi boste preusmerjeni na Pregled in lahko začnete uporabljati aplikacijo.

---

### Prva prijava z namenskimi poverilnicami AMS

Če ste prejeli namenske poverilnice za storitev Area Monitoring Service:

#### Korak 1: Odprite aplikacijo

1. Odprite spletni brskalnik
2. Pojdite na [https://area-monitoring.planet.com](https://area-monitoring.planet.com)
3. Prikaže se prijavni zaslon

![Prijavni zaslon z označenimi polji za uporabniško ime in geslo](../imgs/login-ams.png)

#### Korak 2: Vnesite poverilnice

1. Vnesite svoje **uporabniško ime** ali **e-pošto**
2. Vnesite svoje **geslo**
3. Kliknite `Sign In`

#### Korak 3: Sprejem pogojev uporabe

1. Ob prvi prijavi preberite pogoje uporabe Planet Labs
2. Označite potrditveno polje
3. Za nadaljevanje kliknite `Accept`

#### Korak 4: Dostop odobren

Po overitvi boste preusmerjeni na Pregled in lahko začnete uporabljati aplikacijo.

## Izbira obsega

Po prijavi je vaša prva naloga izbira obsega (Scope):

1. V levem delu glave aplikacije poiščite **spustni meni obsega**
2. Kliknite spustni meni za prikaz razpoložljivih obsegov _(če je na voljo le en obseg, spustni meni ni prikazan)_

![Spustni meni za izbiro obsega z označenim obsegom `DEMO_FR23`](../imgs/scope-selection.png)

### Izbira obsega

- `DEMO_FR23`: Demonstracijski obseg, ki je na voljo vsem uporabnikom z računom Planet
- **Projektno specifični obsegi**, dostop do katerih mora odobriti skrbnik uporabnikov danega obsega

1. Kliknite ime obsega, da ga izberete
2. Aplikacija naloži območja zanimanja (FOI) in podatke za izbrani obseg
3. Vsi nadaljnji podatki in filtri bodo specifični za ta obseg

**Pomembno**: Izbrani obseg določa:

- Katero geografsko območje spremljate
- Katera časovna obdobja imajo razpoložljive podatke
- Kateri signali in Markerji se izračunavajo
- Vse možnosti filtriranja po celotni aplikaciji

Obseg lahko kadar koli spremenite s ponovno uporabo spustnega menija obsega.

## Pregled postavitve aplikacije

**Modul Pregled** je vaša glavna vstopna točka po izbiri obsega. Ponuja:

- Pregled vseh območij zanimanja (FOI) v vašem obsegu na visoki ravni
- Prostorski zemljevid z lokacijami območij zanimanja
- Hiter dostop do podrobne analize območja zanimanja (Pogled FOI)
- Več načinov iskanja in filtriranja območij zanimanja
- Prilagodljivo postavitev — z vlečenjem ločilnika med tabelo in zemljevidom prilagodite njuno razmerje

Modul Pregled uporabljajte za:

- Brskanje in raziskovanje območij zanimanja v vašem območju spremljanja
- Prepoznavanje območij zanimanja, ki vas zanimajo
- Navigacijo do podrobne analize v modulu Pogled FOI

![Pregled](../imgs/overview.png)

### Glavne komponente

**Glava** (zgoraj):

- **Spustni meni obsega**: Preklapljanje med razpoložljivimi obsegi; pri delu v Validacijski konzoli sta tu prikazana tudi aktivni ID svežnja (Bundle) in ID paketa (Package)
- **Preklop teme**: temna ali svetla
- **Meni aplikacij**: Preklop na druge aplikacije Planet
- **Uporabniški meni**: Dostop do profila, nastavitev jezika in odjave

**Zložljiv meni modulov** (levo) — glavni delovni prostor se spreminja glede na izbrani modul:

- **Podatkovni pogled**:
  - **Pregled** (privzeto): Seznam in zemljevid območij zanimanja
  - **Pogled FOI**: Podrobna analiza območja zanimanja z več paneli
- **Validacijska konzola (Validation)** (vidna uporabnikom z dostopom do Validacijske konzole):
  - **Ustvarjanje svežnja**: Določitev vrst vhodov in filtrov za ustvarjanje svežnja paketov _(samo vloga: GTC-ADMIN)_
  - **Svežnji**: Seznam ustvarjenih svežnjev _(vloge: GTC-ADMIN, GTC-OPERATOR ali GTC-VIEWER)_
  - **Paketi**: Seznam ustvarjenih paketov _(vloge: GTC-ADMIN, GTC-OPERATOR ali GTC-VIEWER)_
  - **Validacijski pogled**: Analiza območja zanimanja na ravni paketa z več paneli in uporabniškim vnosom _(vloge: GTC-ADMIN, GTC-OPERATOR ali GTC-VIEWER)_
- **Konfiguracija**:
  - **Nastavitve**: Nastavitev časovnega obsega in konteksta markerjev
  - **Urejevalnik konfiguracije** (Config Editor): Urejanje konfiguracije obsega _(samo vloga: SUPER-SYSADMIN)_

Osrednje območje prikazuje komponente, specifične za izbrani modul.

![Glavne komponente](../imgs/main-components.png)

## Izbira jezika

Aplikacija je na voljo v angleščini, češčini in slovenščini.

Za preklop jezika:

1. Kliknite ikono **Uporabniškega menija** v zgornjem desnem kotu glave
2. Postavite kazalec na podmeni **Jezik**
3. Izberite želeni jezik

Vmesnik se posodobi takoj. Vaša izbira jezika se ohrani med sejami.

## Nastavitev časovnega obsega in konteksta markerjev

Pred analizo podatkov nastavite časovne in markerske parametre v predalu **Nastavitve**.

### Odpiranje predala Nastavitve

1. Kliknite **Nastavitve** v zložljivem meniju modulov (na dnu levega panela, pod **Konfiguracija**)

![Predal Nastavitve](../imgs/settings.png)

### Nastavitev časovnega obsega

1. V predalu Nastavitve poiščite razdelek **Časovni obseg**
2. Nastavite **Datum začetka**: kliknite koledar in izberite začetni datum
3. Nastavite **Datum konca**: kliknite koledar in izberite končni datum
4. Po želji nastavite **Izbrani datum**: kliknite koledar in izberite datum
   - Osnovna plast zemljevida se posodobi in prikaže posnetke z izbranega datuma
   - Uporabno za prikaz prostorskega konteksta v določenem trenutku

**Pomembno**: Podatki se prikažejo le, če v izbranem časovnem obdobju obstajajo veljavna opazovanja (Observation). Če ne vidite podatkov, poskusite prilagoditi časovni obseg tako, da bo ustrezal obdobju zbiranja podatkov. Ob prvem odpiranju obsega je časovni obseg nastavljen na privzeti začetni in končni datum, določena v konfiguraciji obsega.

### Izbira konteksta markerjev

1. V predalu Nastavitve poiščite spustni meni **Kontekst markerjev**
2. Kliknite spustni meni za prikaz razpoložljivih kontekstov markerjev
3. Izberite želeni kontekst

Konteksti markerjev omogočajo prikaz različnih naborov izračunov markerjev, ki običajno predstavljajo različne izračunske izvedbe ali sezonska obdobja.

**Opomba**: Nastavitve zdaj nadzorujejo le **časovni obseg** in **kontekst markerjev**. Možnosti prikaza opazovanj (stolpci, plast, odmik, filter veljavnosti) ter možnosti vizualizacije signalov in markerjev so dostopne neposredno znotraj vsake komponente — glejte [Modul Pogled FOI](03-foi-view-module.mdx).

## Zemljevidni gradnik in navigacija

**Zemljevidni gradnik** prikazuje območja zanimanja s prostorskim kontekstom in interaktivnimi orodji.

### Pregled vmesnika zemljevida

Zemljevid v modulu Pregled prikazuje prostorsko razporeditev vaših območij zanimanja.

#### Funkcije zemljevida

- **Poligoni območij zanimanja**: Meje polj, prikazane na zemljevidu
- **Barvno kodiranje**: Območja zanimanja so lahko obarvana z eno barvo, glede na atribute območja zanimanja ali glede na rezultate markerjev
- **Izbrano območje zanimanja**: privzeto označeno z rumenim obrisom
- **Osnovna plast**: osnovne plasti, določene v konfiguraciji obsega
- **Prekrivanje s šestkotno mrežo H3**: ob oddaljitvi pod prag približanja območij zanimanja so območja zanimanja združena v šestkotne celice. Kliknite šestkotno celico za približanje in prikaz območij zanimanja, ki jih vsebuje.

#### Upravljanje zemljevida

- **Premikanje**: Kliknite in povlecite za premikanje po zemljevidu
- **Približevanje**: Za približevanje ali oddaljevanje uporabite gumba za približanje ali kolesce miške
- **Pojdi na vse**: Gumb za približanje na prikaz vseh območij zanimanja v obsegu

### Iskanje po zemljevidu (Pojdi na)

Vrstica **Iskanje po zemljevidu** na zemljevidu omogoča neposredno navigacijo do določenega območja zanimanja ali koordinatne lokacije. Podpira samodejno dokončanje in ob neujemanju prikaže sporočilo »Ni rezultatov«.

#### Navigacija po ID-ju območja zanimanja

1. Kliknite iskalno vrstico na zemljevidu
2. Vnesite **ID območja zanimanja** (oblika: `SCOPE.FOI.123`) ali začnite tipkati **referenčni ID območja zanimanja**
3. Iz spustnega seznama izberite ustrezen rezultat
4. Zemljevid se osredini na to območje zanimanja in to postane izbrano

#### Navigacija po koordinatah

1. Kliknite iskalno vrstico na zemljevidu
2. Vnesite par koordinat v eni od treh podprtih oblik:
   - **Decimalne stopinje** (EPSG:4326): `45.5, 10.2`
   - **Kardinalne stopinje** (EPSG:4326): `45.5°N, 10.2°E`
   - **Projicirane metrične koordinate** (CRS obsega): `500000, 5500000`
3. Iz spustnega seznama izberite koordinatni rezultat
4. Zemljevid se premakne na to lokacijo

### Panel Predogled izbire

Z levim klikom na območje zanimanja na zemljevidu se odpre plavajoči panel **Predogled izbire**, ki prikazuje povzetek atributov območja zanimanja — vključno z njegovimi ključnimi identifikatorji. To deluje dosledno v Pregledu, Pogledu FOI in Validacijskem pogledu ter je najhitrejši način za prepoznavanje parcele in branje njenih podrobnosti brez polnega odpiranja.

- Če se na mestu klika prekriva več območij zanimanja, jih panel vsa našteje; kliknite enega za razširitev njegovih atributov
- Natančni prikazani atributi so odvisni od preslikave podatkov obsega (glejte [Konfiguracija obsega](05-scope-configuration.mdx)), zato lahko različni obsegi razkrivajo različna polja
- Kliknite
  - **Izberi območje zanimanja** za izbiro predogledanega območja zanimanja
  - **Odpri v novem zavihku Pogleda FOI** za prehod v podrobno analizo v novem zavihku brskalnika
  - **Kopiraj** za kopiranje ID-ja območja zanimanja

![Panel Predogled izbire, odprt po kliku na parcelo na zemljevidu](../imgs/map-selection-preview.png)

### Orodja

**Merilno orodje**:

1. Kliknite ikono **merilnega orodja**
2. Izberite način **Razdalja** ali **Površina**
3. **Za razdaljo**:
   - Klikajte točke na zemljevidu za izris črte
   - Razdalja se izračuna in prikaže (v metrih ali km)
   - Za zaključek dvakrat kliknite
4. **Za površino**:
   - Klikajte točke za izris poligona
   - Površina se izračuna in prikaže — prikazana enota je odvisna od konfiguracije obsega (m², km², **ha** ali ari)
   - Za zaključek zaprite poligon

![Merilno orodje](../imgs/map-measure-tool.png)

**Orodje za postavitev oznake**:

1. Kliknite ikono **postavitve oznake**
2. Kliknite kjer koli na zemljevidu, da postavite oznako
3. Prikažejo se **natančne koordinate** oznake
4. To uporabite za kopiranje koordinat
5. Kliknite `X` na oznaki, da jo odstranite

![Orodje za postavitev oznake](../imgs/map-drop-pin.png)

**Ustvari foto nalogo**:

Če ima vaš račun dovoljenja za ustvarjanje foto nalog, je na zemljevidu na voljo gumb **Ustvari foto nalogo**. Najprej izberite območje zanimanja, nato kliknite gumb, da odprete upravitelj foto nalog za to polje. Ta gumb je skrit, kadar dostop do fotografij za obseg ni omogočen.

**Premikanje in približevanje**:

- **Premikanje**: Kliknite in povlecite zemljevid za premikanje
- **Približevanje**: Uporabite gumba za približanje ali kolesce miške
- **Pojdi na območje zanimanja**: Gumb za osredinjenje in približanje na izbrano območje zanimanja

### Upravljanje plasti

#### Osnovna plast

**Sprememba osnovne plasti ozadja**:

1. Kliknite gumb ali ikono **Plasti**
2. Odpre se panel za upravljanje plasti
3. Izberite med razpoložljivimi osnovnimi plastmi:
   - **Letalska fotografija**: Letalski posnetki visoke ločljivosti
   - **Posnetki Sentinel ali Planet**: plasti, določene v povezani konfiguraciji Planet Insight Platform
   - Druge plasti, specifične za obseg, določene v njegovi konfiguraciji
4. Ozadje zemljevida se posodobi takoj

![Upravljanje osnovne plasti](../imgs/map-base-layers.png)

**Opomba — imena plasti in privzete vrednosti izhajajo iz konfiguracije obsega**: Seznam razpoložljivih osnovnih plasti, njihova imena in privzeto naložena plast so določeni s strani skrbnika obsega. Če je ime plasti videti napačno ali zastarelo ali se plast sploh ne naloži, gre za zadevo konfiguracije obsega — ne za nekaj, kar bi lahko popravili v aplikaciji. Obrnite se na svojega skrbnika (glejte [Konfiguracija obsega](05-scope-configuration.mdx)) ali se obrnite na podporo Planet (glejte [Podpora in odpravljanje težav](07-support-troubleshooting.mdx)).

#### Možnosti vizualizacije območij zanimanja

**Preklapljanje oznak in obrisov za območja zanimanja**:

1. Odprite panel **Nastavitve plasti**
2. Poiščite možnosti **Vizualizacija območij zanimanja**:
   - **Prikaži oznake**: Prikaz atributnih oznak območja zanimanja (npr. ID območja zanimanja, vrsta posevka). Oznake se prikažejo, ko se približate prek praga približanja, nastavljenega za posamezno vrsto območja zanimanja v obsegu.
   - **Prikaži obrise**: Prikaz obrisov poligonov območij zanimanja
   - **Prikaži zapolnitev**: Zapolnitev poligonov območij zanimanja z barvo
   - **Prosojnost**: Nastavitev prosojnosti (0–100 %)
3. Vklapljajte in izklapljajte možnosti za prilagoditev prikaza

**Obarvaj območja zanimanja po**:
Območja zanimanja lahko obarvate z različnimi shemami:

1. **Ena barva**:
   - Iz spustnega seznama izberite eno od razpoložljivih barv
   - Vsa območja zanimanja so vizualizirana z izbrano barvo

2. **Atributi**:
   - Iz spustnega seznama izberite enega od razpoložljivih atributov
   - Barve predstavljajo različne vrednosti izbranega atributa (npr. vrsta posevka)

3. **Rezultati markerjev**:
   - Območja zanimanja so obarvana glede na napovedi markerjev
   - Iz spustnega seznama izberite enega od razpoložljivih markerjev
   - Primeri: zelena za »odobreno«, rdeča za »označeno«
   - Barve so določene s konfiguracijo obsega

![Možnosti vizualizacije območij zanimanja](../imgs/map-foi-visualization-options.png)

**Opomba — privzete vrednosti oznak izhajajo iz konfiguracije obsega**: Katere oznake so prikazane in ali so privzeto vklopljene, je nastavljeno za vsako vrsto območja zanimanja v konfiguraciji obsega. Če so oznake ob odpiranju aplikacije že vidne, gre za pričakovano privzeto nastavitev za vaš obseg — vaš skrbnik jih je tako nastavil. Uporabnik lahko te privzete nastavitve prikaza kadar koli prilagodi svojim željam in potrebam.

**Poudarek izbranega območja zanimanja**:

- Trenutno izbrano območje zanimanja je privzeto poudarjeno z **rumenim obrisom in zapolnitvijo**
- Olajša prepoznavanje, katero območje zanimanja analizirate
- Barvo obrisa lahko spremenite v možnostih plasti

## Gradnik seznama/tabele območij zanimanja

### Funkcije

**Filtrirljiva tabela**:

- Vsa območja zanimanja v trenutnem obsegu, prikazana v tabeli
- Z **poljem za filtriranje** na vrhu filtrirajte območja zanimanja
- Izberite kateri koli atribut in vnesite vrednost za hitro iskanje območij zanimanja

**Upravljanje stolpcev**:

- Kliknite **Upravljaj stolpce** za prikaz ali skritje stolpcev
- Prilagodite, kateri atributi območja zanimanja so prikazani
- Stolpci lahko vključujejo: ID območja zanimanja, Vrsta, Posevek, Raba zemljišča, Površina itd.

**Oštevilčenje strani**:

- Pri velikem številu območij zanimanja se pomikajte med stranmi
- Številke strani so prikazane na dnu tabele

**Hitro preklapljanje območij zanimanja**:

- Kliknite katero koli vrstico območja zanimanja, da preklopite nanj
- Trenutno izbrano območje zanimanja je v tabeli poudarjeno

![Filter območij zanimanja](../imgs/foi-filter-columns.png)

## Iskanje in izbira območij zanimanja

Aplikacija AM ponuja več načinov za izbiro območij zanimanja. Izberite metodo, ki najbolje ustreza vašemu delovnemu postopku:

### Metoda 1: Izbira na seznamu območij zanimanja

1. Poglejte panel **Seznam območij zanimanja** (na levi strani)
2. Kliknite območje zanimanja na seznamu
3. Zemljevid se osredini na to območje zanimanja in njegovi podatki se naložijo
4. Kliknite akcijski gumb za vstop v podroben Pogled FOI

### Metoda 2: Filter

Vrstica filtra omogoča zožitev seznama območij zanimanja po eni ali več vrednostih atributov.

Na primer, če poznate vrsto območja zanimanja in referenčni ID:

1. Poglejte panel **Seznam območij zanimanja** (na levi strani)
2. **Uporabite vrstico filtra** na vrhu seznama
3. S spustnim menijem za izbiro atributa izberite atribut za filtriranje
4. Z možnostjo **Vrsta FOI** izberite vrsto (npr. »Polje«, »Parcela«)
5. Z možnostjo **Referenčni ID FOI** vnesite enega ali več referenčnih ID-jev v polje filtra
6. Seznam se posodobi in prikaže le ujemajoča se območja zanimanja
7. Kliknite akcijski gumb za vstop v podroben Pogled FOI

![Filter območij zanimanja](../imgs/foi-filter-foi-type-ref-id.png)

### Metoda 3: Iskanje po zemljevidu

Z vrstico **Iskanje po zemljevidu** na zemljevidu skočite neposredno na območje zanimanja po njegovem ID-ju ali referenčnem ID-ju ali navigirajte do niza koordinat. Podrobnosti glejte v [Zemljevidni gradnik in navigacija](#zemljevidni-gradnik-in-navigacija).

### Metoda 4: Interakcija z zemljevidom

Območja zanimanja izberite neposredno z zemljevida:

1. Poiščite svoje območje zanimanja na prikazu zemljevida
2. **Z levim klikom** na poligon območja zanimanja odprite panel **Predogled izbire** z njegovimi atributi
3. **Z desnim klikom** na poligon območja zanimanja pojdite neposredno v podroben Pogled FOI

**Namig**: Premikajte in približujte zemljevid za raziskovanje različnih območij. Ob oddaljitvi kliknite šestkotno celico za približanje na to skupino območij zanimanja.

Napredne funkcije analize območja zanimanja so opisane v [Modul Pogled FOI](03-foi-view-module.mdx).

---
